import { PublicLayout } from '../layout/PublicLayout';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router';
import { ArrowRight, FileText } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';
import { BLOG_POSTS } from '../data/blogPosts';

export const BlogPage = () => {
  useSEO({
    title: 'Raisy Blog - AI Workforce, Bots, and Process Automation | Raisy',
    description:
      'How Raisy builds an AI workforce: the company operating system, the ROI math, and how bots earn autonomy. Research-backed posts on AI agents and process orchestration.',
    keywords:
      'AI company operating system, AI workforce platform, human-directed AI agents, AI agent lifecycle, Raisy blog',
    ogTitle: 'Raisy Blog - AI Workforce, Bots, and Process Automation',
    ogDescription: 'Research-backed posts on the Raisy AI workforce platform.',
    canonicalUrl: 'https://raisy.team/blog',
  });

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-20">
        {/* Hero image — real product visuals, brand colors */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-12">
          <img
            src="/ultra-wide-panoramic-cgi-dashboard-render-for-ai-w_thumbnail.jpg"
            alt="The Raisy Blog: everything prepared, you just decide"
            className="w-full object-cover"
          />
        </div>

        <div className="max-w-3xl mb-12">
          <Badge className="mb-4 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 hover:bg-cyan-100 dark:hover:bg-cyan-900/40">
            The Raisy Blog
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Everything prepared. You just decide.
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-300">
            How Raisy builds an AI company operating system: the architecture, the ROI math, and how
            bots earn the right to run autonomously. Every number is auditable.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white dark:bg-slate-900 rounded-xl shadow hover:shadow-lg transition-all overflow-hidden border border-slate-200 dark:border-slate-700"
            >
              <div className="overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-44 object-cover group-hover:scale-[1.03] transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase tracking-wide text-cyan-600 dark:text-cyan-400">
                    {post.category}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {new Date(post.date + 'T00:00:00Z').toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      timeZone: 'UTC',
                    })}
                  </span>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white group-hover:text-cyan-700 dark:group-hover:text-cyan-400">
                  {post.title}
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{post.excerpt}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-cyan-600 dark:text-cyan-400">
                  Read <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PublicLayout>
  );
};
